import { ApiResponse } from "../models/api-response";
import { ApiOptions } from "../types/api.types";
import { formatApiErrorBody } from "../utils/api-error";

/**
 * A shared helper function for making API calls using fetch
 * Handles common functionality like credentials, error handling, and response parsing
 * 
 * @param url The URL to make the request to
 * @param options Request options including method, headers, body, and responseType
 * @returns ApiResponse containing the response data or error
 */
export const callApi = async <T>(
  url: string, 
  options: ApiOptions
): Promise<ApiResponse<T>> => {
  try {
    // Exclude responseType from fetch options since it's not a standard fetch option
    const { responseType, ...fetchOptions } = options;
    
    const response = await fetch(url, {
      ...fetchOptions,
      credentials: "include",
    });
    
    if (!response.ok) {
      if (response.status === 401 && typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent('invirtus:auth-unauthorized', { detail: { url, source: 'callApi' } })
        );
      }
      const errorData = await response.json().catch(() => ({}));
      const fromBody = formatApiErrorBody(errorData);
      return {
        data: undefined,
        error: fromBody || `Error: ${response.status} ${response.statusText}`,
      };
    }
    
    // Handle different response types
    let data;
    // Check for empty response (common with 204 No Content responses)
    const contentType = response.headers.get('content-type');
    
    if (response.status === 204 || !contentType || response.headers.get('content-length') === '0') {
      // Return null for empty responses
      data = null;
    } else if (responseType === 'blob') {
      data = await response.blob();
    } else if (contentType && contentType.includes('application/json')) {
      // Only try to parse as JSON if the content type is JSON
      data = await response.json();
    } else {
      // For other content types, return as text
      data = await response.text();
    }
    
    return {
      data: data as T,
      error: undefined,
    };
  } catch (err) {
    console.error('API call error:', err);
    return {
      data: undefined,
      error: err instanceof Error ? err.message : "An unexpected error occurred",
    };
  }
}; 
